import type { CargaDoPainel, ItemDaFila, SituacaoDaCarga } from './contrato';

/**
 * Textos e rótulos do catálogo de veículo em leitura humana. Sem dependência
 * de tela: as telas da Parametrização e as deste módulo importam daqui.
 */

const TIPOS_CONHECIDOS: Readonly<Record<string, string>> = {
  MICROONIBUS: 'Micro-ônibus',
  ONIBUS: 'Ônibus',
  CAMIONETA: 'Camioneta',
  CAMINHONETE: 'Caminhonete',
  UTILITARIO: 'Utilitário',
};

/** Tipo de veículo como o arquivo da fonte escreve: MICROONIBUS -> Micro-ônibus. */
export function rotuloDoTipo(tipo: string): string {
  const limpo = tipo.trim();
  return TIPOS_CONHECIDOS[limpo] ?? limpo.charAt(0) + limpo.slice(1).toLowerCase();
}

/** Lista de tipos separada por vírgula, como o catálogo guarda ("MICROONIBUS, ONIBUS"). */
export function rotuloDosTipos(tipos: string | null | undefined): string {
  if (!tipos) return '—';
  return tipos
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map(rotuloDoTipo)
    .join(', ');
}

/**
 * Página do conjunto de dados para a pessoa abrir: o endereço que a rotina usa
 * é o da API do portal (`/api/3/action/package_show?id=frota-ativa`), que
 * devolve dados técnicos; a página humana é `/dataset/frota-ativa`.
 */
export function paginaDoConjunto(endereco: string): string {
  try {
    const url = new URL(endereco);
    const id = url.searchParams.get('id');
    if (url.pathname.endsWith('/package_show') && id) {
      return `${url.origin}/dataset/${encodeURIComponent(id)}`;
    }
    return endereco;
  } catch {
    return endereco;
  }
}

export const ROTULO_DA_PENDENCIA: Readonly<Record<ItemDaFila['pendencia'], string>> = {
  MARCA: 'Marca desconhecida',
  MODELO: 'Modelo desconhecido',
};

export const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDaCarga, string>> = {
  PEDIDA: 'Pedida',
  EM_ANDAMENTO: 'Em andamento',
  SEM_NOVIDADE: 'Sem arquivo novo',
  APLICADA: 'Aplicada',
  FALHOU: 'Falhou',
};

export const VARIANTE_DA_SITUACAO: Readonly<
  Record<SituacaoDaCarga, 'default' | 'secondary' | 'destructive' | 'outline'>
> = {
  PEDIDA: 'outline',
  EM_ANDAMENTO: 'outline',
  SEM_NOVIDADE: 'secondary',
  APLICADA: 'default',
  FALHOU: 'destructive',
};

/** O que a execução fez, numa frase. */
export function resumoDaCarga(carga: CargaDoPainel): string {
  if (carga.situacao === 'FALHOU') return carga.mensagem ?? 'A execução falhou.';
  if (carga.situacao === 'SEM_NOVIDADE') {
    return 'A fonte não publicou arquivo novo desde a última carga. Nada mudou.';
  }
  if (carga.situacao !== 'APLICADA') return 'Aguardando a leitura da fonte.';
  const c = carga.contagens;
  const partes = [
    `${c.textosNoArquivo ?? 0} textos no arquivo`,
    `${c.novos ?? 0} novos`,
    `${c.alterados ?? 0} alterados`,
    `${c.naoVistos ?? 0} não vistos`,
    `${c.naFila ?? 0} na fila`,
  ];
  if (c.catalogoAtualizado) partes.push(`${c.catalogoAtualizado} itens do catálogo atualizados`);
  if (c.sugestoes) partes.push(`${c.sugestoes} sugestões em itens protegidos`);
  return `${partes.join(', ')}.`;
}

/**
 * O que a pessoa lê no modal "Como funciona a integração". Em linguagem
 * simples, para quem não é da área técnica, e só com o que o sistema de fato
 * faz (rotina da API `catalogo-veiculos` e migração 0400).
 */
export const SECOES_DA_INTEGRACAO: readonly { titulo: string; texto: string }[] = [
  {
    titulo: 'De onde vêm os dados',
    texto:
      'A fonte oficial é a Frota Ativa do DETRAN-SP, publicada uma vez por mês no portal de dados abertos do Estado de São Paulo, com licença CC BY 4.0 (uso livre, citando a fonte). O TEG considera só os veículos de passageiros do tipo micro-ônibus e ônibus registrados no município de São Paulo; a administração pode ajustar esse recorte. A frota nacional da Senatran ainda não é consultada.',
  },
  {
    titulo: 'Consulta diária',
    texto:
      'Todo dia, às 5h, o sistema pergunta à fonte se ela publicou arquivo novo. Se não publicou, nada muda. A administração também pode pedir a consulta na hora, pelo botão "Atualizar agora".',
  },
  {
    titulo: 'Só as diferenças',
    texto:
      'Quando sai arquivo novo, o sistema compara com o que já tem: inclui o que é novo e corrige o que mudou. O que continua igual não é tocado.',
  },
  {
    titulo: 'Nada é apagado sozinho',
    texto:
      'Se uma marca, um modelo ou uma versão deixa de aparecer na fonte, ele não some do TEG: fica marcado como "não visto na fonte desde" a data do arquivo. Só uma pessoa pode desativar um item.',
  },
  {
    titulo: 'Fila de grafias',
    texto:
      'A fonte escreve os nomes como no documento do veículo, por exemplo "VW" ou "M.BENZ". Cada marca, modelo e versão do TEG tem um nome oficial e uma lista de grafias equivalentes. Grafia que o sistema não conhece não vira cadastro sozinha: vai para a fila de conferência, e a administração decide se é outra grafia de um item que já existe ou um item novo.',
  },
  {
    titulo: 'Edição à mão fica protegida',
    texto:
      'Quando alguém corrige um item pela tela, ele fica protegido: a consulta automática não muda mais esse item e só registra o que a fonte sugere, para a administração decidir.',
  },
  {
    titulo: 'Histórico',
    texto:
      'Toda alteração fica registrada: quem fez (a consulta automática ou a pessoa), quando, o valor anterior, o valor novo e de qual arquivo da fonte veio.',
  },
];
